import { Select } from 'antd';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import type { Field, FieldWithValue } from 'api/tasks';
import { Tag } from 'components/atoms/tag';
import { FieldHelper } from 'utils/field';
import styles from './select.module.css';

interface Props {
  mode?: 'multiple';
  field: FieldWithValue;
  value?: number[];
  onChange: (selected: number | number[], field: Field) => void;
}

const StyledTag = styled(Tag)`
  margin-bottom: var(--spacing-01);
  margin-right: var(--spacing-01);
`;

export const SelectField: FC<Props> = ({ mode, field, value, onChange }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleOnChange = (selected: number | number[]) => {
    onChange(selected, field);
  };

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  const {
    options: { activeOptions, allOptions },
  } = FieldHelper(field);

  const tagRender = (_props) => {
    const { value: _value, closable, onClose } = _props;
    const opt = allOptions?.find((o) => o.value === _value);

    const handleTagClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleIsOpen();
    };

    /* NOTE:: Supported colours of tag based on background colour.
    key = background-colour, value: text colour */
    /* const colorHash: { [x: string]: Color } = {
      '#EDF5FF': 'blue',
      '#E0E0E0': 'gray',
      '#E5F6FF': 'cyan',
      '#F4F4F4': 'cool-gray',
      '#DEFBE6': 'green',
      '#FFF0F7': 'magenta',
      '#F6F2FF': 'purple',
      '#FFF1F1': 'red',
      '#F7F3F2': 'gray',
      '#D9FBFB': 'teal',
    }; */

    return opt ? (
      <StyledTag
        onClick={handleTagClick}
        key={_value}
        color={opt.color}
        closable={closable}
        onClose={onClose}
        data-cy={`task-section-fields.${field.fieldLabel}`}
      >
        {opt.label}
      </StyledTag>
    ) : (
      <></>
    );
  };

  return (
    <Select
      showSearch={false}
      allowClear
      mode={mode}
      open={isOpen}
      onDropdownVisibleChange={(visible) => setIsOpen(visible)}
      showArrow={false}
      bordered={false}
      size="small"
      tagRender={tagRender}
      className={styles.select}
      dropdownClassName="task-field"
      placeholder="—"
      optionFilterProp="children"
      onChange={handleOnChange}
      value={value || []}
      filterOption={(input, option) =>
        option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
    >
      {activeOptions?.map((option) => (
        <Select.Option value={option.value} key={option.value}>
          {option.color != null ? (
            <Tag color={option.color}>{option.label}</Tag>
          ) : (
            option.label
          )}
        </Select.Option>
      ))}
    </Select>
  );
};
